import { FC } from 'react';
import styles from './sparkline.module.scss';

interface SparklinePoint {
  x: number;
  y: number;
}

export interface SparklineProps {
  /** Primary series, rendered as a solid line. */
  points: SparklinePoint[];
  /** Optional projection past the primary series, rendered as a dashed continuation. */
  projectedPoints?: SparklinePoint[];
  /** SVG width in pixels. Default 260. */
  width?: number;
  /** SVG height in pixels. Default 48. */
  height?: number;
  /** Accessible label. */
  ariaLabel?: string;
}

/**
 * Inline SVG sparkline. Renders the primary series as a solid line and the
 * optional projection as a dashed continuation, with a vertical divider at the
 * join and a horizontal zero-baseline if the combined range crosses zero. All
 * stroke colors come from Carbon tokens (set in the SCSS module).
 *
 * Both series share one x/y scale so the projection reads as a continuation.
 * The component is generic — it knows nothing about curves, dates, or
 * differentials. The caller is responsible for translating its domain data
 * into `{x, y}` points (e.g., x = month index from series start).
 *
 * Returns null when the primary series has fewer than 2 points (nothing
 * meaningful to draw).
 * @param {SparklineProps} props - The sparkline props
 * @return {JSX.Element | null} The sparkline SVG or null
 */
export const Sparkline: FC<SparklineProps> = ({
  points,
  projectedPoints = [],
  width = 260,
  height = 48,
  ariaLabel = 'Value trend',
}) => {
  if (points.length < 2) return null;

  const all = [...points, ...projectedPoints];
  // reduce, not Math.min(...spread): a daily-frequency import can exceed V8's
  // argument-count limit and throw RangeError on the spread.
  const yMin = all.reduce((min, p) => Math.min(min, p.y), Infinity);
  const yMax = all.reduce((max, p) => Math.max(max, p.y), -Infinity);
  const range = yMax - yMin || 1;

  const pad = 3;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  const totalLen = points.length + projectedPoints.length;
  const denom = totalLen - 1 || 1;

  const project = (
    p: SparklinePoint,
    indexFromStart: number
  ): [number, number] => {
    const x = pad + (indexFromStart / denom) * innerW;
    const y = pad + (1 - (p.y - yMin) / range) * innerH;
    return [x, y];
  };

  const solidCoords = points.map((p, i) => project(p, i));
  const solidPath = solidCoords
    .map(
      ([x, y], i) =>
        `${i === 0 ? 'M' : 'L'}${Number(x.toFixed(1))},${Number(y.toFixed(1))}`
    )
    .join(' ');

  let projectedPath: string | null = null;
  let joinX: number | null = null;
  if (projectedPoints.length > 0) {
    const joinCoord = solidCoords[solidCoords.length - 1];
    joinX = joinCoord[0];
    const projectedCoords = projectedPoints.map((p, i) =>
      project(p, points.length + i)
    );
    projectedPath = [joinCoord, ...projectedCoords]
      .map(
        ([x, y], i) =>
          `${i === 0 ? 'M' : 'L'}${Number(x.toFixed(1))},${Number(
            y.toFixed(1)
          )}`
      )
      .join(' ');
  }

  let zeroBaselineY: number | null = null;
  if (yMin < 0 && yMax > 0) {
    zeroBaselineY = pad + (1 - (0 - yMin) / range) * innerH;
  }

  return (
    <svg
      className={styles.sparkline}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
    >
      {zeroBaselineY !== null && (
        <line
          className={styles.zeroBaseline}
          x1={pad}
          y1={zeroBaselineY}
          x2={width - pad}
          y2={zeroBaselineY}
          strokeDasharray="2 2"
          strokeWidth={1}
        />
      )}
      {joinX !== null && (
        <line
          className={styles.divider}
          x1={joinX}
          y1={pad}
          x2={joinX}
          y2={height - pad}
          strokeDasharray="1 2"
          strokeWidth={1}
        />
      )}
      <path
        className={styles.solid}
        d={solidPath}
        fill="none"
        strokeWidth={1.5}
      />
      {projectedPath && (
        <path
          className={styles.projected}
          d={projectedPath}
          fill="none"
          strokeWidth={1.5}
          strokeDasharray="3 2"
        />
      )}
    </svg>
  );
};
